import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, CircularProgress, Alert, Button, Grid, Typography, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { authGet } from '../../../services/apiService';
import CustomerDetailView from './CustomerDetailView';
import TopCustomersOpenCard from './TopCustomersOpenCard';
import OpenAgeCard from '../OpenAgeCard';
import { HEALTH_COLORS } from './customerHealth';

const DEEP_GREEN = '#1A2E22';
const MUTED = '#666666';
const INK = '#1A1A1A';
const NEUTRAL_SURFACE = '#F8F9FA';


const formatAge = (days) => {
  if (days == null || Number.isNaN(Number(days))) return '—';
  const n = Math.max(0, Math.round(Number(days)));
  return `${n}d`;
};

/**
 * Board subline — only signal, never “Healthy” wallpaper.
 * Stalled New 24h+ · fresh New under 24h · Attention / Critical only.
 */
const boardAccountSubline = (customer) => {
  const caseNo = customer.newestCaseNumber;
  const ageDays = customer.newestCaseAgeDays;
  if (caseNo && ageDays != null && Number(ageDays) >= 1) {
    return {
      text: `${caseNo} · not moved to progress (${formatAge(ageDays)})`,
      color: '#c85a00',
      fontWeight: 600
    };
  }
  if (caseNo) {
    return {
      text: `New ${caseNo} · under 24h`,
      color: '#2e7d32',
      fontWeight: 600
    };
  }
  const health = customer.health?.status;
  if (health === 'attention' || health === 'critical') {
    const tone = HEALTH_COLORS[health];
    return {
      text: customer.health?.label || health,
      color: tone.color,
      fontWeight: 600
    };
  }
  return null;
};

/**
 * Soft row wash — not full green/red floods (those bury Attention in a green wall).
 * Healthy = quiet tint · Attention = warm wash · Critical = red wash.
 */
const boardRowTone = (customer) => {
  const health = customer.health?.status;
  const stalledNew = Boolean(
    customer.newestCaseNumber
    && customer.newestCaseAgeDays != null
    && Number(customer.newestCaseAgeDays) >= 1
  );
  if (health === 'critical') {
    return { bg: HEALTH_COLORS.critical.bg, accent: HEALTH_COLORS.critical.color, hover: '#f8d7d5' };
  }
  if (health === 'attention' || stalledNew) {
    return { bg: HEALTH_COLORS.attention.bg, accent: HEALTH_COLORS.attention.color, hover: '#f5dccb' };
  }
  if (health === 'healthy') {
    return { bg: '#f4f9f5', accent: 'rgba(46,125,50,0.35)', hover: '#e8f2ea' };
  }
  return { bg: '#fff', accent: 'transparent', hover: 'rgba(26,46,34,0.05)' };
};

/** Quiet zeros; bold non-zeros so the eye jumps to signal. */
const CountCell = ({ value, hotColor = INK }) => {
  const n = Number(value) || 0;
  const hot = n > 0;
  return (
    <TableCell align="center">
      <Typography
        component="span"
        sx={{
          fontWeight: hot ? 800 : 400,
          fontSize: '0.875rem',
          color: hot ? hotColor : 'rgba(26,46,34,0.28)'
        }}
      >
        {n}
      </Typography>
    </TableCell>
  );
};

// Same table style as the L2 Open list: theme cell padding, 0.875rem text.
const HEAD_CELL_SX = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: DEEP_GREEN,
  bgcolor: NEUTRAL_SURFACE,
  borderBottom: '2px solid rgba(26,46,34,0.12)',
  whiteSpace: 'nowrap'
};

/** One customer per row. Deployment Group and UAT Instance have no data source yet, so they stay empty. */
const CustomerListTable = ({ customers, onSelectCustomer }) => (
  <Box sx={{ width: '100%' }}>
    <TableContainer>
      <Table aria-label="Top customers" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_CELL_SX, width: "26%" }}>Account</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "7%" }}>New</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "7%" }}>Open</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "10%" }}>Code Version</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "13%" }}>Deployment Group</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "13%" }}>UAT Instance</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "8%" }}>Last 30 Days</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "8%" }}>31-60 Days</TableCell>
            <TableCell align="center" sx={{ ...HEAD_CELL_SX, width: "8%" }}>61-90 Days</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer) => {
            const subline = boardAccountSubline(customer);
            const tone = boardRowTone(customer);
            const quiet = !subline && !(customer.new || customer.openStatus || customer.wip);
            return (
              <TableRow
                key={customer.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectCustomer(customer.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectCustomer(customer.id);
                  }
                }}
                // Tinted per cell, not on the row: the theme's zebra stripe (.MuiTableRow-root:nth-of-type(even))
                // out-ranks a row-level background, so every other row would lose its tint.
                sx={{
                  cursor: 'pointer',
                  '& > td': { bgcolor: tone.bg, borderBottom: '1px solid rgba(26,46,34,0.08)' },
                  '& > td:first-of-type': { borderLeft: `3px solid ${tone.accent}` },
                  '&:hover > td': { bgcolor: tone.hover },
                  outline: 'none',
                  '&:focus-visible': { outline: `2px solid ${DEEP_GREEN}` }
                }}
              >
                <TableCell>
                  <Typography
                    sx={{
                      fontWeight: quiet ? 500 : 700,
                      fontSize: '0.875rem',
                      color: quiet ? MUTED : INK,
                      lineHeight: 1.3,
                      wordBreak: 'break-word'
                    }}
                  >
                    {customer.name}
                  </Typography>
                  {subline ? (
                    <Typography
                      title={subline.text}
                      sx={{
                        fontSize: '0.75rem',
                        color: subline.color,
                        fontWeight: subline.fontWeight,
                        lineHeight: 1.3,
                        wordBreak: 'break-word'
                      }}
                    >
                      {subline.text}
                    </Typography>
                  ) : null}
                </TableCell>
                <CountCell value={customer.new} hotColor="#2e7d32" />
                <CountCell value={(Number(customer.openStatus) || 0) + (Number(customer.wip) || 0)} />
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.875rem', color: customer.codeVersion ? INK : MUTED }}>
                    {customer.codeVersion || '—'}
                  </Typography>
                </TableCell>
                <TableCell />
                <TableCell />
                <CountCell value={customer.created30} />
                <CountCell value={customer.created31to60} />
                <CountCell value={customer.created61to90} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const TopCustomersList = ({ customers, openSummary, onSelectCustomer }) => {
  const sorted = useMemo(() => {
    const rows = [...customers];
    const openCount = (c) => (Number(c.openStatus) || 0) + (Number(c.wip) || 0);
    rows.sort((a, b) => {
      const openDiff = openCount(b) - openCount(a);
      if (openDiff !== 0) return openDiff;
      const last30Diff = (Number(b.created30) || 0) - (Number(a.created30) || 0);
      if (last30Diff !== 0) return last30Diff;
      const ar = Number(a.rank);
      const br = Number(b.rank);
      const aOk = Number.isFinite(ar) && ar >= 1;
      const bOk = Number.isFinite(br) && br >= 1;
      if (aOk && bOk && ar !== br) return ar - br;
      if (aOk && !bOk) return -1;
      if (!aOk && bOk) return 1;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
    return rows;
  }, [customers]);

  return (
    <Box sx={{ width: '100%' }}>
      {openSummary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TopCustomersOpenCard open={openSummary.open} linked={openSummary.linked} excluded={openSummary.excludedCustomers} versions={openSummary.byCodeVersion} />
          </Grid>
          <Grid item xs={12} md={8}>
            <OpenAgeCard counts={openSummary.byAge} oldestDays={openSummary.oldestAge} />
          </Grid>
        </Grid>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Customers
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
          {sorted.length} {sorted.length === 1 ? 'customer' : 'customers'}
        </Typography>
      </Box>

      {sorted.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: '0.85rem', py: 2, textAlign: 'center' }}>
          No customers match this filter.
        </Typography>
      ) : (
        <CustomerListTable customers={sorted} onSelectCustomer={onSelectCustomer} />
      )}
      </Paper>
    </Box>
  );
};

/**
 * Top Customers — lake-backed by Admin-configured ACCOUNT_IDs.
 * List is lean (fast); detail paints from Mongo first, then ADO ages refresh in background.
 */
const TopCustomersPage = ({ OpenCasesTable = null, onLoaded = null, refreshKey = 0 }) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [openSummary, setOpenSummary] = useState(null);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [agesRefreshing, setAgesRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const detailRequestIdRef = useRef(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authGet('/api/customer-issues/top-customers', null, 'json', { useCache: false });
      if (!res || res.success === false) {
        throw new Error((res && res.message) || 'Failed to load Top Customers');
      }
      setCustomers(res.customers || []);
      setOpenSummary(res.openSummary || null);
      // No openSummary (e.g. frontend deployed before backend) is a failure, not a loading tile.
      if (onLoaded) {
        onLoaded(
          res.openSummary ? res.openSummary.open : null,
          !res.openSummary,
          res.openSummary ? res.openSummary.excludedCustomers : 0
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to load Top Customers from Data Lake');
      setCustomers([]);
      setOpenSummary(null);
      if (onLoaded) onLoaded(null, true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // The parent bumps refreshKey after a Sync: reload the list (tile, age card, versions) and any open customer.
  useEffect(() => {
    if (!refreshKey) return;
    load();
    if (selectedCustomerId) openCustomer(selectedCustomerId);
  }, [refreshKey]);

  const openCustomer = async (id) => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setSelectedCustomerId(id);
    setDetailCustomer(null);
    setDetailLoading(true);
    setAgesRefreshing(false);
    setError(null);
    try {
      // Fast path: Mongo + local WorkItem dates only (no live ADO wait).
      const fast = await authGet(
        `/api/customer-issues/top-customers/${encodeURIComponent(id)}?liveAdo=0`,
        null,
        'json',
        { useCache: false }
      );
      if (detailRequestIdRef.current !== requestId) return;
      if (!fast || fast.success === false || !fast.customer) {
        throw new Error((fast && fast.message) || 'Failed to load customer detail');
      }
      setDetailCustomer(fast.customer);
      setDetailLoading(false);

      // Background: pull ADO CreatedDate and refresh L1/L2/L3 ages when ready.
      setAgesRefreshing(true);
      authGet(
        `/api/customer-issues/top-customers/${encodeURIComponent(id)}?liveAdo=1`,
        null,
        'json',
        { useCache: false }
      )
        .then((full) => {
          if (detailRequestIdRef.current !== requestId) return;
          if (full && full.success !== false && full.customer) {
            setDetailCustomer(full.customer);
          }
        })
        .catch(() => {
          /* keep fast payload; ages may stay blank until next open */
        })
        .finally(() => {
          if (detailRequestIdRef.current === requestId) setAgesRefreshing(false);
        });
    } catch (err) {
      if (detailRequestIdRef.current !== requestId) return;
      setError(err.message || 'Failed to load customer detail');
      setSelectedCustomerId(null);
      setDetailCustomer(null);
      setDetailLoading(false);
      setAgesRefreshing(false);
    }
  };

  const backToList = () => {
    detailRequestIdRef.current += 1;
    setSelectedCustomerId(null);
    setDetailCustomer(null);
    setDetailLoading(false);
    setAgesRefreshing(false);
  };

  if (loading || detailLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !selectedCustomerId) {
    return (
      <Box sx={{ maxWidth: 560 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={load} sx={{ textTransform: 'none' }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {detailCustomer ? (
        <CustomerDetailView
          customer={detailCustomer}
          onBack={backToList}
          agesRefreshing={agesRefreshing}
          OpenCasesTable={OpenCasesTable}
        />
      ) : (
        <TopCustomersList
          customers={customers}
          openSummary={openSummary}
          onSelectCustomer={openCustomer}
        />
      )}
    </Box>
  );
};

export default TopCustomersPage;
